<template>
    <component
        :is="componentName"
        :meta="meta"
        :action="action"
        :args="args"
        :locked-rows="lockedRows"
        :to-remove-toolbar-items-names="toRemoveToolbarItemsNames"
        :toolbar-user-items="toolbarUserItems"
        :dots-click="dotsClick"
        :hasnt-route="hasntRoute"
        :addCustomClick="addCustomClick"
        @user-settings-changed="onUserSettingsChanged"
        @grid-focus-changed="onGridFocusChanged"
        @user-data-changed="onUserDataChanged"
        @refresh="onRefreshHandler"
        @delete-click="onDeleteClick"
        @mounted="onMountedHandler"
        @toolbar-button-click="e => emit('toolbarButtonClick', e)"
        @cell-click="onCellClick"
        @save-settings="onSaveSettingsHandler"
        @editGridMounted="onEditGridMounted"
        @changesSavedSuccessfully="onChangesSavedSuccessfully"
        @changesRemoved="onChangesRemoved"
        @onAddClickEvent="handleAddClickEvent"
        ref="gridEditRef"
    >
        <template v-for="(_, name) in $slots" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps"></slot>
        </template>
    </component>
</template>

<script>
export default {
    name: 'ai-grid-edit-wrapper',
};
</script>

<script setup>
import { ref, onMounted } from 'vue';
//import aiGridEdit from '../ai-grid-edit/ai-grid-edit.vue';
import { mainState, setIsReloadValue } from '@/layouts/main-layout/main-layout-data';
import { getUserDataChanges, getUserSettingsChanges, setUserSettingsChanges } from '@/service/userdata-service';
import { tabState } from '@/layouts/main-layout/components/navigation-bars-data';

const gridEditRef = ref();
const isComponentMounted = ref(false);
const componentName = 'ai-grid-edit';
const state = {
    userSettings: null,
};

const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    action: {
        type: Function,
        default: null,
    },
    args: {
        type: Object,
        default: () => {},
    },
    lockedRows: {
        type: Array,
        default: () => [],
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => ['choice', 'edit'],
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    dotsClick: {
        type: Function,
        default: metaCell => {
            console.log('ai-grid-edit default dotsClick(metaCell) metaCell: ' + JSON.stringify({}));
            return {};
        },
    },
    hasntRoute: {
        type: Boolean,
        default: false,
    },
    addCustomClick: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'onAddClickEvent',
    'gridEditWrapperMounted',
    'refresh',
    'deleteClick',
    'userDataChanged',
    'toolbarButtonClick',
    'userSettingsChanged',
    'dataLoaded',
    'changesSavedSuccessfully',
    'changesRemoved',
    'userDataChangedLoaded',
    'cellClick',
]);

onMounted(async () => {});

const onEditGridMounted = async () => {
    if (mainState.isReload || props.hasntRoute) {
        await gridEditRef.value.setDataSource();
        await fetchAndSetUserSettings({ viewId: props.meta.viewId });

        emit('dataLoaded');
        setIsReloadValue(false);
    }

    if (props.meta.viewId === mainState.viewId) {
        await gridEditRef.value.setDataSource();
        await fetchAndSetUserSettings({ viewId: props.meta.viewId });
        emit('dataLoaded');
    }

    emit('gridEditWrapperMounted');
};

const onChangesRemoved = () => {
    emit('changesRemoved');
};

const handleAddClickEvent = e => {
    emit('onAddClickEvent', e);
};

const setUserDataFromCacheOrApi = async (args, isNeedResult) => {
    let userChanges;
    if (Object.keys(tabState).length && tabState[window.location.pathname]?.changes) {
        userChanges = isNeedResult
            ? { result: true, gridChanges: tabState[window.location.pathname].changes }
            : tabState[window.location.pathname].changes;
    } else {
        const { result, changes } = await getUserDataChanges({ viewId: props.meta.viewId });

        userChanges = isNeedResult ? { result, changes } : changes;

        tabState[window.location.pathname] ??= {};
        tabState[window.location.pathname].changes = changes;
    }

    const changes = Array.isArray(userChanges) ? userChanges : userChanges?.gridChanges;

    if (changes?.length) {
        gridEditRef.value?.setUserDataChanges(changes, true);
    }

    if (userChanges?.rowKey) {
        gridEditRef.value?.setFocusdRow(userChanges?.rowKey);
    }

    emit('userDataChangedLoaded', userChanges);
    return userChanges;
};

const fetchAndSetUserSettings = async () => {
    if (props.hasntRoute) {
        const { result, settings } = await getUserSettingsChanges({ viewId: props.meta.viewId });
        result ? (state.userSettings = settings) : (state.userSettings = {});

        tabState[window.location.pathname] ??= {};
        tabState[window.location.pathname].settings = settings;
        return;
    }
    if (Object.keys(tabState).length && tabState[window.location.pathname]?.settings) {
        state.userSettings = tabState[window.location.pathname].settings;
        gridEditRef.value?.gridInstance.state({ ...state.userSettings });
    } else {
        const { result, settings } = await getUserSettingsChanges({ viewId: props.meta.viewId });
        if (result && settings) {
            state.userSettings = settings;
            gridEditRef.value?.gridInstance.state({ ...state.userSettings });
        }

        tabState[window.location.pathname] ??= {};
        tabState[window.location.pathname].settings = settings;
    }
};

const getUserSettings = () => {
    return { ...state.userSettings };
};

const onChangesSavedSuccessfully = () => {
    emit('changesSavedSuccessfully');
};

const onSaveSettingsHandler = async () => {
    const userSettings = getUserSettings();

    tabState[window.location.pathname] ??= {};
    if (userSettings) {
        tabState[window.location.pathname].settings = userSettings.settings;
    } else {
        tabState[window.location.pathname].settings = null;
    }

    await setUserSettingsChanges({ settings: userSettings, viewId: props.meta.viewId });
};

const onMountedHandler = async () => {
    if (isComponentMounted.value) {
        await gridEditRef.value.setCacheOrApiDataSource();
        emit('dataLoaded');
        isComponentMounted.value = false;
    }
};

const onRefreshHandler = () => {
    emit('refresh');
};

const onDeleteClick = rowsToDelete => {
    emit('deleteClick', rowsToDelete);
};

const onUserDataChanged = args => {
    emit('userDataChanged', args);
};
const onGridFocusChanged = (row, viewId, rowIndex, component) => {
    emit('gridFocusChanged', row, viewId, rowIndex, component);
};
const onUserSettingsChanged = async (userSettings, autoSaveMode) => {
    if (autoSaveMode) {
        tabState[window.location.pathname] ??= {};
        if (userSettings) {
            tabState[window.location.pathname].settings = userSettings.settings;
        } else {
            tabState[window.location.pathname].settings = null;
        }

        await setUserSettingsChanges(userSettings);
    } else {
        state.userSettings = userSettings.settings;
    }

    emit('userSettingsChanged', userSettings, autoSaveMode);
};

const onCellClick = async (viewId, key) => {
    emit('cellClick', viewId, key);
};

defineExpose({
    ref: gridEditRef,
    setUserDataFromCacheOrApi,
    fetchAndSetUserSettings,
});
</script>
